import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Edit2, Package, Search, Filter, CheckCircle, XCircle, ExternalLink, Clock, Award, BarChart3, Users, Phone, MousePointerClick, TrendingUp, Globe } from 'lucide-react';
import { API, authFetch } from '@/App';
import { toast } from 'sonner';
import GlowBadge from '@/components/GlowBadge';
import BrandAutocomplete from '@/components/BrandAutocomplete';

export default function AdminPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [pendingSubmissions, setPendingSubmissions] = useState([]);
  const [badgeRequests, setBadgeRequests] = useState([]);
  const [activeTab, setActiveTab] = useState('products'); // products, analytics, history
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [productHistory, setProductHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [editingHistoryProduct, setEditingHistoryProduct] = useState(null);
  const [historyEditForm, setHistoryEditForm] = useState({});
  const [productStats, setProductStats] = useState({ total: 0 });
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    category: 'skincare',
    product_type: 'cleanser',
    price: '',
    image: '',
    purchase_url: ''
  });

  useEffect(() => {
    fetchUser();
    fetchProducts();
    fetchPendingSubmissions();
    fetchBadgeRequests();
    fetchProductStats();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await authFetch(`${API}/auth/me`);
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await authFetch(`${API}/products`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProductStats = async () => {
    try {
      const response = await authFetch(`${API}/products/stats`);
      if (response.ok) setProductStats(await response.json());
    } catch (e) { /* ignore */ }
  };


  const fetchProductHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await authFetch(`${API}/admin/product-history`);
      if (response.ok) {
        setProductHistory(await response.json());
      }
    } catch (error) {
      console.error('Error fetching product history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSaveHistoryEdit = async (item) => {
    try {
      const endpoint = item.product_id
        ? `${API}/admin/products/${item.product_id}`
        : null;

      if (!endpoint) {
        toast.error('Cannot edit this item');
        return;
      }

      const priceValue = parseFloat((historyEditForm.price || '0').replace(/[₹,]/g, ''));

      const response = await authFetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...historyEditForm,
          price_value: isNaN(priceValue) ? 0 : priceValue
        })
      });

      if (response.ok) {
        toast.success('Product updated');
        setEditingHistoryProduct(null);
        fetchProductHistory();
        fetchProducts();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update product');
    }
  };

  const productTypes = {
    skincare: ['cleanser', 'toner', 'serum', 'moisturizer', 'sunscreen', 'exfoliator', 'face_mask', 'eye_cream', 'lip_balm', 'body_lotion', 'body_wash', 'skin_treatment'],
    makeup: ['lipstick', 'lip_gloss', 'lip_liner', 'foundation', 'concealer', 'primer', 'blush', 'highlighter', 'bronzer', 'eyeshadow', 'eyeliner', 'eyebrow_pencil', 'kajal', 'mascara', 'compact', 'setting_spray', 'nail_polish'],
    haircare: ['shampoo', 'conditioner', 'hair_oil', 'hair_serum', 'hair_treatment', 'hair_mask', 'hair_color', 'dry_shampoo'],
    pharmacy: ['derma_product', 'pharma_skincare', 'pharma_haircare', 'supplement']
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.brand || !formData.price) {
      toast.error('Please fill all required fields');
      return;
    }

    if (formData.product_type === 'other' && !formData.custom_type?.trim()) {
      toast.error('Please specify the custom product type');
      return;
    }

    try {
      const endpoint = editingProduct 
        ? `${API}/admin/products/${editingProduct.product_id}`
        : `${API}/admin/products`;
      
      const method = editingProduct ? 'PUT' : 'POST';
      
      const priceValue = parseFloat(formData.price.replace(/[₹,]/g, ''));

      const payload = { ...formData };
      if (payload.product_type === 'other' && payload.custom_type) {
        payload.product_type = payload.custom_type.toLowerCase().replace(/\s+/g, '_');
      }
      delete payload.custom_type;
      
      const response = await authFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...payload,
          price: `₹${priceValue}`,
          price_value: priceValue
        })
      });

      if (response.ok) {
        toast.success(editingProduct ? 'Product updated!' : 'Product added!');
        setShowAddForm(false);
        setEditingProduct(null);
        resetForm();
        fetchProducts();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save product');
      }
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    }
  };

  const handleDelete = async (productId) => {
    if (!confirm('Are you sure you want to delete this product?')) return;
    
    try {
      const response = await authFetch(`${API}/admin/products/${productId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success('Product deleted');
        fetchProducts();
      } else {
        toast.error('Failed to delete product');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    }
  };

  const fetchPendingSubmissions = async () => {
    try {
      const response = await authFetch(`${API}/admin/product-submissions`);
      if (response.ok) setPendingSubmissions(await response.json());
    } catch (error) {
      console.error('Error fetching submissions:', error);
    }
  };

  const handleReviewSubmission = async (submissionId, action) => {
    try {
      const response = await authFetch(`${API}/admin/product-submissions/${submissionId}/${action}`, {
        method: 'POST'
      });
      if (response.ok) {
        toast.success(action === 'approve' ? 'Product approved & added!' : 'Submission rejected');
        fetchPendingSubmissions();
        if (action === 'approve') fetchProducts();
      } else {
        const err = await response.json();
        toast.error(err.detail || `Failed to ${action}`);
      }
    } catch (error) {
      toast.error(`Failed to ${action} submission`);
    }
  };

  const fetchBadgeRequests = async () => {
    try {
      const response = await authFetch(`${API}/admin/badge-requests`);
      if (response.ok) setBadgeRequests(await response.json());
    } catch (error) {
      console.error('Error fetching badge requests:', error);
    }
  };

  const handleBadgeAction = async (requestId, action, tier = 'glow') => {
    try {
      const body = action === 'approve' ? { tier } : { reason: 'Not eligible at this time' };
      const response = await authFetch(`${API}/admin/badge-requests/${requestId}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (response.ok) {
        toast.success(action === 'approve' ? `Badge approved as ${tier}` : 'Request rejected');
        fetchBadgeRequests();
      } else {
        const err = await response.json();
        toast.error(err.detail || `Failed to ${action}`);
      }
    } catch (error) {
      toast.error(`Failed to ${action} badge request`);
    }
  };

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const response = await authFetch(`${API}/admin/analytics`);
      if (response.ok) setAnalytics(await response.json());
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleEdit = (product) => {
    setFormData({
      name: product.name,
      brand: product.brand,
      category: product.category,
      product_type: product.product_type,
      price: product.price?.replace('₹', '') || '',
      image: product.image || '',
      purchase_url: product.purchase_url || ''
    });
    setEditingProduct(product);
    setShowAddForm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      brand: '',
      category: 'skincare',
      product_type: 'cleanser',
      price: '',
      image: '',
      purchase_url: ''
    });
  };

  // Filter products
  const filteredProducts = products.filter(product => {
    const matchesSearch = !searchQuery || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.brand.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Group by category for stats
  const stats = products.reduce((acc, p) => {
    acc[p.category] = (acc[p.category] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button data-testid="back-btn" onClick={() => navigate(-1)} className="p-2 hover:bg-primary/10 rounded-full transition-colors">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Admin</h1>
          </div>
          {activeTab === 'products' && (
            <button
              data-testid="add-product-btn"
              onClick={() => { resetForm(); setEditingProduct(null); setShowAddForm(true); }}
              className="btn-primary flex items-center gap-2 px-4 py-2"
            >
              <Plus className="w-5 h-5" />
              Add Product
            </button>
          )}
        </div>
        {/* Tabs */}
        <div className="max-w-4xl mx-auto px-4 flex gap-1">
          {[
            { key: 'products', label: 'Products', icon: Package },
            { key: 'history', label: 'History', icon: Clock },
            { key: 'analytics', label: 'Analytics', icon: BarChart3 },
          ].map(tab => (
            <button
              key={tab.key}
              data-testid={`tab-${tab.key}`}
              onClick={() => {
                setActiveTab(tab.key);
                if (tab.key === 'analytics' && !analytics) fetchAnalytics();
                if (tab.key === 'history' && productHistory.length === 0) fetchProductHistory();
              }}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">

        {/* === ANALYTICS TAB === */}
        {activeTab === 'analytics' && (
          analyticsLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : analytics ? (
            <div className="space-y-6">
              {/* Login Funnel */}
              <div className="bg-white rounded-xl border border-border p-5" data-testid="login-funnel">
                <h3 className="font-bold text-base mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <MousePointerClick className="w-5 h-5 text-primary" /> Login Page Funnel (7 days)
                </h3>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                  {[
                    { label: 'Page Views', val: analytics.login_funnel.page_views, color: 'text-blue-600' },
                    { label: 'Google Clicks', val: analytics.login_funnel.google_clicks, color: 'text-red-500' },
                    { label: 'Phone Clicks', val: analytics.login_funnel.phone_clicks, color: 'text-green-600' },
                    { label: 'OTP Sent', val: analytics.login_funnel.otp_sent, color: 'text-amber-600' },
                    { label: 'OTP Verified', val: analytics.login_funnel.otp_verified, color: 'text-emerald-600' },
                    { label: 'Alt Login', val: analytics.login_funnel.alternate_clicks, color: 'text-purple-600' },
                  ].map((item) => (
                    <div key={item.label} className="text-center p-2 bg-gray-50 rounded-lg">
                      <p className={`text-xl font-bold ${item.color}`}>{item.val}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Referrers */}
              {analytics.referrers?.length > 0 && (
                <div className="bg-white rounded-xl border border-border p-5" data-testid="referrers">
                  <h3 className="font-bold text-base mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <Globe className="w-5 h-5 text-blue-500" /> Top Referrers
                  </h3>
                  <div className="space-y-2">
                    {analytics.referrers.map((r) => (
                      <div key={r.url} className="flex justify-between items-center text-sm py-1.5 border-b border-gray-100 last:border-0">
                        <span className="text-muted-foreground truncate max-w-[250px]">{r.url}</span>
                        <span className="font-semibold">{r.visits}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Product Clicks */}
              <div className="bg-white rounded-xl border border-border p-5" data-testid="product-clicks">
                <h3 className="font-bold text-base mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <TrendingUp className="w-5 h-5 text-orange-500" /> Product Clicks (30 days) — Total: {analytics.product_clicks.total}
                </h3>
                {analytics.product_clicks.top_clicked.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-muted-foreground text-xs border-b">
                          <th className="pb-2 pr-3">#</th>
                          <th className="pb-2 pr-3">Product</th>
                          <th className="pb-2 pr-3">Brand</th>
                          <th className="pb-2 pr-3 text-right">Clicks</th>
                          <th className="pb-2 text-right">Unique Users</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.product_clicks.top_clicked.map((p) => (
                          <tr key={p.product_name} className="border-b border-gray-50 last:border-0">
                            <td className="py-2 pr-3 text-muted-foreground">{i + 1}</td>
                            <td className="py-2 pr-3 font-medium max-w-[200px] truncate">{p.product_name}</td>
                            <td className="py-2 pr-3 text-muted-foreground">{p.brand}</td>
                            <td className="py-2 pr-3 text-right font-bold text-primary">{p.clicks}</td>
                            <td className="py-2 text-right">{p.unique_users}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-4 text-center">No product clicks yet. Clicks are tracked when users tap products in checklists.</p>
                )}

                {analytics.product_clicks.by_source.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs font-semibold text-muted-foreground mb-2">Clicks by Source</p>
                    <div className="flex flex-wrap gap-2">
                      {analytics.product_clicks.by_source.map((s) => (
                        <span key={s.source} className="text-xs bg-gray-100 px-2.5 py-1 rounded-full">
                          {s.source}: <strong>{s.clicks}</strong>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Checklist Shares */}
              {analytics.checklist_shares?.length > 0 && (
                <div className="bg-white rounded-xl border border-border p-5" data-testid="checklist-shares">
                  <h3 className="font-bold text-base mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <ExternalLink className="w-5 h-5 text-green-500" /> Top Shared Checklists (30 days)
                  </h3>
                  <div className="space-y-2">
                    {analytics.checklist_shares.map((s) => (
                      <div key={s.checklist_id || s.name} className="flex justify-between items-center text-sm py-1.5 border-b border-gray-100 last:border-0">
                        <span className="font-medium">{s.name || s.checklist_id}</span>
                        <span className="font-bold text-primary">{s.shares} shares</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* User List with Phone Numbers */}
              <div className="bg-white rounded-xl border border-border p-5" data-testid="user-list">
                <h3 className="font-bold text-base mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Users className="w-5 h-5 text-violet-500" /> Users ({analytics.users.length})
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-muted-foreground text-xs border-b">
                        <th className="pb-2 pr-3">Name</th>
                        <th className="pb-2 pr-3">Email</th>
                        <th className="pb-2 pr-3"><Phone className="w-3 h-3 inline" /> Phone</th>
                        <th className="pb-2 pr-3">Badge</th>
                        <th className="pb-2 pr-3 text-right">Followers</th>
                        <th className="pb-2 text-right">Checklists</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.users.map((u, i) => (
                        <tr key={i} className="border-b border-gray-50 last:border-0 hover:bg-gray-50">
                          <td className="py-2 pr-3 font-medium max-w-[120px] truncate">{u.name || '—'}</td>
                          <td className="py-2 pr-3 text-muted-foreground text-xs max-w-[150px] truncate">{u.email || '—'}</td>
                          <td className="py-2 pr-3 font-mono text-xs">{u.phone || '—'}</td>
                          <td className="py-2 pr-3">{u.glow_badge ? <GlowBadge tier={u.glow_badge} size="xs" /> : '—'}</td>
                          <td className="py-2 pr-3 text-right">{u.follower_count || 0}</td>
                          <td className="py-2 text-right">{u.checklist_count || 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-10">Failed to load analytics</p>
          )
        )}

        {/* === HISTORY TAB === */}
        {activeTab === 'history' && (
          historyLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Products Added History</h2>
                <span className="text-sm text-muted-foreground">{productHistory.length} entries</span>
              </div>

              {productHistory.length === 0 ? (
                <p className="text-center text-muted-foreground py-10">No product history yet</p>
              ) : (
                <div className="space-y-3">
                  {productHistory.map((item, idx) => (
                    <div key={item.submission_id || item.product_id || idx} className="bg-white rounded-xl border border-border p-4 space-y-2" data-testid={`history-item-${idx}`}>
                      {editingHistoryProduct === (item.submission_id || item.product_id) ? (
                        <div className="space-y-2">
                          <input
                            data-testid={`history-edit-name-${idx}`}
                            value={historyEditForm.name || ''}
                            onChange={(e) => setHistoryEditForm({...historyEditForm, name: e.target.value})}
                            className="w-full px-3 py-2 rounded-lg border border-border text-sm"
                            placeholder="Product name"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              data-testid={`history-edit-brand-${idx}`}
                              value={historyEditForm.brand || ''}
                              onChange={(e) => setHistoryEditForm({...historyEditForm, brand: e.target.value})}
                              className="px-3 py-2 rounded-lg border border-border text-sm"
                              placeholder="Brand"
                            />
                            <input
                              data-testid={`history-edit-price-${idx}`}
                              value={historyEditForm.price || ''}
                              onChange={(e) => setHistoryEditForm({...historyEditForm, price: e.target.value})}
                              className="px-3 py-2 rounded-lg border border-border text-sm"
                              placeholder="Price"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <select
                              data-testid={`history-edit-category-${idx}`}
                              value={historyEditForm.category || 'skincare'}
                              onChange={(e) => setHistoryEditForm({...historyEditForm, category: e.target.value, product_type: productTypes[e.target.value]?.[0] || historyEditForm.product_type})}
                              className="px-3 py-2 rounded-lg border border-border text-sm"
                            >
                              <option value="skincare">Face Care</option>
                              <option value="makeup">Make Up</option>
                              <option value="haircare">Hair Care</option>
                              <option value="pharmacy">Pharma</option>
                            </select>
                            <select
                              data-testid={`history-edit-type-${idx}`}
                              value={historyEditForm.product_type || ''}
                              onChange={(e) => setHistoryEditForm({...historyEditForm, product_type: e.target.value})}
                              className="px-3 py-2 rounded-lg border border-border text-sm"
                            >
                              {(productTypes[historyEditForm.category] || productTypes.skincare).map(type => (
                                <option key={type} value={type}>{type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</option>
                              ))}
                            </select>
                          </div>
                          <input
                            data-testid={`history-edit-url-${idx}`}
                            value={historyEditForm.purchase_url || ''}
                            onChange={(e) => setHistoryEditForm({...historyEditForm, purchase_url: e.target.value})}
                            className="w-full px-3 py-2 rounded-lg border border-border text-sm"
                            placeholder="Purchase URL"
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveHistoryEdit(item)}
                              className="px-4 py-2 bg-primary text-white rounded-lg text-xs font-medium hover:bg-primary/90"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingHistoryProduct(null)}
                              className="px-4 py-2 bg-gray-200 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-300"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-sm truncate">{item.name}</p>
                              <p className="text-xs text-muted-foreground">{item.brand} · {item.product_type?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')} · {item.price || 'N/A'}</p>
                            </div>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${
                              item.status === 'approved' || item.status === 'auto_approved' || item.status === 'added'
                                ? 'bg-green-50 text-green-600'
                                : item.status === 'pending'
                                ? 'bg-amber-50 text-amber-600'
                                : 'bg-red-50 text-red-600'
                            }`}>
                              {item.status === 'auto_approved' ? 'Auto-Approved' : item.status}
                            </span>
                          </div>

                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <div className="flex items-center gap-2">
                              {item.added_by_picture && (
                                <img src={item.added_by_picture} alt="" className="w-5 h-5 rounded-full object-cover" />
                              )}
                              <span>
                                {item.type === 'submission' ? 'Suggested by' : 'Added by'}{' '}
                                <span className="font-medium text-foreground">
                                  {item.added_by_name || 'Unknown'}
                                  {item.added_by_username ? ` (@${item.added_by_username})` : ''}
                                </span>
                              </span>
                            </div>
                            <span>{item.date ? new Date(item.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}</span>
                          </div>

                          {item.edited_at && (
                            <p className="text-xs text-amber-600">Edited: {new Date(item.edited_at).toLocaleDateString('en-IN')}</p>
                          )}

                          <div className="flex items-center gap-2 pt-1">
                            {item.product_id && (
                              <button
                                data-testid={`history-edit-${idx}`}
                                onClick={() => {
                                  setEditingHistoryProduct(item.submission_id || item.product_id);
                                  setHistoryEditForm({
                                    name: item.name, brand: item.brand, category: item.category,
                                    product_type: item.product_type, price: item.price, purchase_url: item.purchase_url
                                  });
                                }}
                                className="text-xs text-primary hover:underline font-medium flex items-center gap-1"
                              >
                                <Edit2 className="w-3 h-3" /> Edit
                              </button>
                            )}
                            {item.purchase_url && (
                              <a
                                href={item.purchase_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-500 hover:underline flex items-center gap-1"
                              >
                                <ExternalLink className="w-3 h-3" /> Link
                              </a>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        )}

        {/* === PRODUCTS TAB === */}
        {activeTab === 'products' && (<>
        {/* Stats */}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-border text-center overflow-hidden">
            <p className="text-lg sm:text-2xl font-bold text-primary truncate" data-testid="stat-total">{productStats.total.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Total</p>
          </div>
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-border text-center overflow-hidden">
            <p className="text-lg sm:text-2xl font-bold text-pink-500 truncate" data-testid="stat-skincare">{(productStats.skincare || 0).toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Face Care</p>
          </div>
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-border text-center overflow-hidden">
            <p className="text-lg sm:text-2xl font-bold text-purple-500 truncate" data-testid="stat-makeup">{(productStats.makeup || 0).toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Make Up</p>
          </div>
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-border text-center overflow-hidden">
            <p className="text-lg sm:text-2xl font-bold text-green-500 truncate" data-testid="stat-haircare">{(productStats.haircare || 0).toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Hair Care</p>
          </div>
          <div className="bg-white rounded-xl p-3 sm:p-4 border border-border text-center overflow-hidden">
            <p className="text-lg sm:text-2xl font-bold text-teal-500 truncate" data-testid="stat-pharmacy">{(productStats.pharmacy || 0).toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">Pharma</p>
          </div>
        </div>

        {/* Pending Submissions */}
        {pendingSubmissions.length > 0 && (
          <div className="bg-amber-50 rounded-2xl p-5 border border-amber-200">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-5 h-5 text-amber-600" />
              <h2 className="text-base font-bold text-amber-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Pending Submissions ({pendingSubmissions.length})
              </h2>
            </div>
            <div className="space-y-3">
              {pendingSubmissions.map(sub => (
                <div key={sub.submission_id} className="bg-white rounded-xl p-4 border border-amber-100">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm">{sub.name}</p>
                      <p className="text-xs text-muted-foreground">{sub.brand} &middot; {sub.category} &middot; {sub.product_type?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</p>
                      <p className="text-xs text-muted-foreground mt-1">By: {sub.user_name} ({sub.user_email})</p>
                      {sub.price && <p className="text-sm font-medium text-primary mt-1">{sub.price}</p>}
                      {sub.verification?.reason && (
                        <p className="text-xs text-amber-600 mt-1">{sub.verification.reason}</p>
                      )}
                      <a href={sub.purchase_url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline flex items-center gap-1 mt-1">
                        <ExternalLink className="w-3 h-3" /> View URL
                      </a>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button
                        data-testid={`approve-${sub.submission_id}`}
                        onClick={() => handleReviewSubmission(sub.submission_id, 'approve')}
                        className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                        title="Approve"
                      >
                        <CheckCircle className="w-5 h-5" />
                      </button>
                      <button
                        data-testid={`reject-${sub.submission_id}`}
                        onClick={() => handleReviewSubmission(sub.submission_id, 'reject')}
                        className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                        title="Reject"
                      >
                        <XCircle className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Badge Requests */}
        {badgeRequests.filter(r => r.status === 'pending').length > 0 && (
          <div className="bg-purple-50 rounded-2xl p-5 border border-purple-200">
            <div className="flex items-center gap-2 mb-3">
              <Award className="w-5 h-5 text-purple-600" />
              <h2 className="text-base font-bold text-purple-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Badge Requests ({badgeRequests.filter(r => r.status === 'pending').length})
              </h2>
            </div>
            <div className="space-y-3">
              {badgeRequests.filter(r => r.status === 'pending').map(req => (
                <div key={req.request_id} className="bg-white rounded-xl p-4 border border-purple-100">
                  <div className="flex items-start gap-3">
                    {req.picture ? (
                      <img src={req.picture} alt={req.name} className="w-10 h-10 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white font-bold shrink-0">
                        {req.name?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm">{req.name} (@{req.username})</p>
                      <p className="text-xs text-muted-foreground">{req.followers_count} followers</p>
                      <p className="text-sm text-foreground mt-2 bg-gray-50 p-2 rounded-lg">{req.reason}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3 justify-end">
                    <button
                      data-testid={`approve-badge-glow-${req.request_id}`}
                      onClick={() => handleBadgeAction(req.request_id, 'approve', 'glow')}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 text-xs font-medium transition-colors"
                    >
                      <GlowBadge tier="glow" size="xs" />
                      Approve Glow
                    </button>
                    <button
                      data-testid={`approve-badge-pro-${req.request_id}`}
                      onClick={() => handleBadgeAction(req.request_id, 'approve', 'glow_pro')}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 text-xs font-medium transition-colors"
                    >
                      <GlowBadge tier="glow_pro" size="xs" />
                      Approve Pro
                    </button>
                    <button
                      data-testid={`reject-badge-${req.request_id}`}
                      onClick={() => handleBadgeAction(req.request_id, 'reject')}
                      className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 text-xs font-medium transition-colors"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Products Header */}
        <div className="border-b border-border pb-2">
          <h3 className="font-bold text-primary flex items-center gap-2">
            <Package className="w-5 h-5" />
            Products ({products.length})
          </h3>
        </div>

        {/* Search & Filter */}
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className="w-full pl-10 pr-4 py-3 rounded-xl border border-border focus:border-primary outline-none"
            />
          </div>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-4 py-3 rounded-xl border border-border focus:border-primary outline-none"
          >
            <option value="all">All Categories</option>
            <option value="skincare">Face Care</option>
            <option value="makeup">Make Up</option>
            <option value="haircare">Hair Care</option>
            <option value="pharmacy">Pharma</option>
          </select>
        </div>

        {/* Product List */}
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Showing {filteredProducts.length} of {productStats.total.toLocaleString()} products
          </p>
          {filteredProducts.map(product => (
            <div key={product.product_id} className="bg-white rounded-xl p-4 border border-border flex items-center gap-4">
              {product.image ? (
                <img 
                  src={product.image} 
                  alt={product.name} 
                  className="w-16 h-16 rounded-lg object-cover shrink-0"
                  onError={(e) => { 
                    e.target.style.display = 'none'; 
                    const fallback = e.target.nextElementSibling;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`w-16 h-16 rounded-lg bg-gradient-to-br from-pink-400 to-orange-300 items-center justify-center text-white font-bold text-lg shrink-0`} style={{ fontFamily: 'Outfit, sans-serif', display: product.image ? 'none' : 'flex' }}>
                g
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate">{product.name}</p>
                <p className="text-xs text-muted-foreground">{product.brand} • {product.category} • {product.product_type}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-sm font-medium text-primary">{product.price}</span>
                  {product.source && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-muted-foreground">{product.source}</span>}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => handleEdit(product)} className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors">
                  <Edit2 className="w-5 h-5" />
                </button>
                <button onClick={() => handleDelete(product.product_id)} className="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors">
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>

      {/* Add/Edit Modal */}
      {showAddForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Product Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Cetaphil Gentle Skin Cleanser"
                  className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary outline-none"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Brand *</label>
                <BrandAutocomplete
                  value={formData.brand}
                  onChange={(brand) => setFormData({ ...formData, brand })}
                  category={formData.category}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Category *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      category: e.target.value,
                      product_type: productTypes[e.target.value][0]
                    })}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary outline-none"
                  >
                    <option value="skincare">Face Care</option>
                    <option value="makeup">Make Up</option>
                    <option value="haircare">Hair Care</option>
                    <option value="pharmacy">Pharma</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Product Type *</label>
                  <select
                    value={formData.product_type}
                    onChange={(e) => setFormData({ ...formData, product_type: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary outline-none"
                  >
                    {productTypes[formData.category].map(type => (
                      <option key={type} value={type}>
                        {type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </option>
                    ))}
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {formData.product_type === 'other' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Custom Type *</label>
                  <input
                    type="text"
                    value={formData.custom_type || ''}
                    onChange={(e) => setFormData({ ...formData, custom_type: e.target.value })}
                    placeholder="e.g., body_mist"
                    className="w-full px-4 py-3 rounded-xl border border-primary bg-primary/5 focus:border-primary outline-none"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium mb-1">Price (INR) *</label>
                <input
                  type="text"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="e.g., 649"
                  className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary outline-none"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Image URL</label>
                <input
                  type="url"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary outline-none"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Tip: Copy image URL from Nykaa/Amazon product page
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Purchase URL</label>
                <input
                  type="url"
                  value={formData.purchase_url}
                  onChange={(e) => setFormData({ ...formData, purchase_url: e.target.value })}
                  placeholder="https://www.nykaa.com/..."
                  className="w-full px-4 py-3 rounded-xl border border-border focus:border-primary outline-none"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingProduct(null);
                  }}
                  className="flex-1 px-4 py-3 rounded-xl border border-border hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 btn-primary px-4 py-3"
                >
                  {editingProduct ? 'Update Product' : 'Add Product'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </>)}
      </div>
    </div>
  );
}
